import React, { useContext } from 'react'
import { Box, Typography, Alert, Paper, Avatar, Card, CardContent, Button } from '@mui/material'
import { WhatsappDataContext } from '../WhatsappMessageContent'
import { WhatsApp, OpenInNew } from '@mui/icons-material'
import { useFormContext } from 'react-hook-form'

const MessagePreview = () => {
    const {selectedTemplate} = useContext(WhatsappDataContext);
    const {watch} = useFormContext();
    const messageType = watch('messageType');
    const template = watch('templateName');  
    const mediaType = watch('mediaType');        
    let previewText = '';
    let headerText = '';
    let headerMedia = null;
    let buttons = [];
    const whatsappTemplateParams = watch('whatsappTemplateParams');
    
    const TemplatePreview = () =>{
        // Process all components first
        selectedTemplate.components?.forEach(component => {
            // Handle HEADER - Text
            if (component.type === 'HEADER' && component.format === 'TEXT' &&  component.text) {
                headerText = component.text;
                // Replace parameters
                Object.keys(whatsappTemplateParams || {}).forEach(key => {
                    if (key.startsWith('header_') && key !== 'header_media') {
                        const index = key.split('_')[1];
                        headerText = headerText.replace(
                            `{{${index}}}`, 
                            whatsappTemplateParams[key] || `{{${index}}}`
                        );
                    }
                });
            }

            // Handle HEADER - Media
            if (component.type === 'HEADER' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format)) {
                const mediaUrl = whatsappTemplateParams?.header_media;
                if (mediaUrl) {
                    headerMedia = {
                        type: component.format.toLowerCase(),
                        url: mediaUrl
                    };
                }
            }
            
            // Handle BODY
            if (component.type === 'BODY') {
                previewText = component.text || '';
                // Replace parameters
                Object.keys(whatsappTemplateParams || {}).forEach(key => {
                    if (key.startsWith('body_')) {
                        const index = key.split('_')[1];
                        previewText = previewText.replace(
                            `{{${index}}}`,
                            whatsappTemplateParams[key] || `{{${index}}}`
                        );
                    }
                });
            }
            
            // Handle BUTTONS
            if (component.type === 'BUTTONS' && component.buttons) {
                buttons = component.buttons.map((button, btnIndex) => {
                    if (button.type === 'URL') {
                        let url = button.url;
                        // Replace button URL parameters
                        Object.keys(whatsappTemplateParams || {}).forEach(key => {
                            if (key.startsWith(`button_${btnIndex}_`)) {
                                const index = key.split('_')[2];
                                url = url.replace(
                                    `{{${index}}}`,
                                    whatsappTemplateParams[key] || `{{${index}}}`
                                );
                            }
                        });
                        return {
                            text: button.text,
                            url: url,
                            type: button.type
                        };
                    }
                    return button;
                });
            }
        });

        // Render header media
        const renderHeaderMedia = () => {
            if (!headerMedia) return null;
            
            const { type, url } = headerMedia;
            
            switch(type) {
                case 'image':
                    return (
                        <Box sx={{ mb: 1, borderRadius: 1, overflow: 'hidden' }}>
                            <img 
                                src={url} 
                                alt="Header Media" 
                                style={{ width: '100%', maxHeight: '200px', objectFit: 'cover' }}
                            />
                        </Box>
                    );
                case 'video':
                    return (
                        <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                📹 Video: {url.split('/').pop()}
                            </Typography>
                        </Box>
                    );
                case 'document':
                    const cleanUrl = url?.split('#')[0]?.split('?')[0] || '';
                    const ext = cleanUrl.split('.').pop()?.toLowerCase();
                    const isPdf = ext === 'pdf';
                    const isOffice = ['doc','docx','xls','xlsx','ppt','pptx'].includes(ext);
                    
                    if (isPdf || isOffice) {
                        const officeViewerSrc = isOffice ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}` : url;
                        return (
                            <Box sx={{ mb: 1 }}>
                                <Box sx={{
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}>
                                    <iframe
                                        title="Document Preview"
                                        src={officeViewerSrc}
                                        style={{ width: '100%', height: '200px', border: 'none' }}
                                    />
                                </Box>
                            </Box>
                        );
                    }
                    
                    return (
                        <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                📄 Document: {url.split('/').pop()}
                            </Typography>
                        </Box>
                    );
                default:
                    return null;
            }
        };

        return (
            <Box>
                { (selectedTemplate && template)?
                <Paper
                  sx={{ 
                      p: 2, 
                      bgcolor: '#E7F5EC', 
                      border: '1px solid #25D366',
                      borderRadius: 2
                  }}
              >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                          {/* Render Header Media */}
                          {renderHeaderMedia()}
                          
                          {/* Render Header Text */}
                          {headerText && (
                              <Typography variant="subtitle2" sx={{ 
                                  fontWeight: 700, 
                                  fontSize: '0.85rem',
                                  mb: 1,
                                  color: '#000'
                              }}>
                                  {headerText}
                              </Typography>
                          )}
                          
                          {/* Render Body Text */}
                          <Typography variant="body2" sx={{ 
                              fontSize: '0.8rem',
                              color: '#000',
                              lineHeight: 1.5,
                              whiteSpace: 'pre-wrap'
                          }}>
                              {previewText}
                          </Typography>
                          
                          {/* Render Buttons */}
                          {buttons.length > 0 && (
                              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {buttons.map((button, index) => (
                                      <Button
                                          key={index}
                                          variant="outlined"
                                          size="small"
                                          endIcon={button.type === 'URL' ? <OpenInNew fontSize="small" /> : null}
                                          sx={{ 
                                              justifyContent: 'center',
                                              textTransform: 'none',
                                              borderColor: '#25D366',
                                              color: '#25D366',
                                              fontSize: '0.75rem',
                                              '&:hover': {
                                                  borderColor: '#128C7E',
                                                  bgcolor: 'rgba(37, 211, 102, 0.04)'
                                              }
                                          }}
                                      >
                                          {button.text}
                                      </Button>
                                  ))}
                              </Box>
                          )}
                      </Box>
                  </Box>
                </Paper>
                : 
                <Alert severity="warning" sx={{ mt: 2, fontSize: '0.7rem' }}>
                    Select a template and fill all parameters to see the preview
                </Alert>
                }
                
            </Box>
        )
    }
    const TextPreview = () => {
        return (
            <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                {watch('message') || 'Message text is required'}
            </Alert>
        )
    }
    const MediaPreview = () =>{
        switch(mediaType){
            case 'image':
                return (
                    watch('mediaUrl') &&
                        <Box sx={{ mb: 1 }}>
                            <img 
                                src={watch('mediaUrl')} 
                                alt="WhatsApp Media Preview" 
                                style={{ maxWidth: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                            />
                        </Box>
                        
                    )
                case 'video':
                case 'audio':
                    return (
                        watch('mediaUrl') &&
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ 
                                fontSize: '0.8rem',
                                color: '#000',
                                lineHeight: 1.5,
                                whiteSpace: 'pre-wrap'
                            }}>
                                {mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} File: {watch('mediaUrl')}
                            </Typography>
                        </Box>
                    )
                case 'document':
                    if (!watch('mediaUrl')) return null;
                    {
                        const url = watch('mediaUrl');
                        const cleanUrl = url?.split('#')[0]?.split('?')[0] || '';
                        const ext = cleanUrl.split('.').pop()?.toLowerCase();
                        const isPdf = ext === 'pdf';
                        const isOffice = ['doc','docx','xls','xlsx','ppt','pptx'].includes(ext);
                        const officeViewerSrc = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;
                        const iframeSrc = isOffice ? officeViewerSrc : url;
                        return (
                            <Box sx={{ mb: 1 }}>
                                <Box sx={{
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}>
                                    <iframe
                                        title="Document Preview"
                                        src={iframeSrc}
                                        style={{ width: '100%', height: '30vh', border: 'none' }}
                                    />
                                </Box>
                                {!isPdf && !isOffice && (
                                    <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem', color: '#000' }}>
                                        Preview unavailable. <a href={url} target="_blank" rel="noopener noreferrer">Open file</a>
                                    </Typography>
                                )}
                            </Box>
                        )
                    }
                default:
                    return null;
        }
    }
  return (
    <Card>
        <CardContent>
        <Box sx={{display:'flex',alignItems:'center',gap:1,mb:1}}>
            <Avatar sx={{ width: '1.5rem', height: '1.5rem', bgcolor: '#25D366',p:0 }}>
                    <WhatsApp sx={{ fontSize: '1rem' }} />
            </Avatar>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                WhatsApp Message Preview:
            </Typography>
        </Box>
        {(selectedTemplate && template)&&
            <TemplatePreview/> 
            
        }
        {messageType === 'text' &&
        
            <TextPreview/>
        }
        {messageType === 'media' && (
            watch('mediaUrl') ? (
                <Paper
                  sx={{ 
                      p: 1, 
                      borderRadius: 2
                  }}
                >
                    {/* image,video,audio,pdf/document preview */}
                    <Box>
                        <Box sx={{ flex: 1 }}>
                            {MediaPreview()}
                        </Box>
                        {watch('caption') && (
                            <Typography
                                variant="body2"
                                sx={{ 
                                    fontSize: '0.8rem',
                                    color: '#000',
                                    lineHeight: 1.5,
                                    whiteSpace: 'pre-wrap',
                                    width:'100%',
                                }}
                            >
                                {watch('caption')}
                            </Typography>
                        )}
                    </Box>
                </Paper>
            ) : (
                <Alert severity="warning" sx={{ mt: 2, fontSize: '0.7rem' }}>
                    {/* Upload media to see the preview */}
                    {!mediaType 
                        ? 'Select Media Type  to see the preview' 
                        : `Upload ${mediaType} Or Enter ${mediaType} URL to see the preview`}
                </Alert>
            )
        )}
        </CardContent>
    </Card>
  )
}

export default MessagePreview
